import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Sparkles, Info, ArrowLeft, Loader2, AlertCircle } from "lucide-react";
import { fmtEUR } from "../lib/storage";

export default function AIGenerateModal({ open, onClose, partidas, loading, error, onAdd, onBack }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (partidas) {
      setItems(
        partidas.map((p) => ({
          ...p,
          _selected: true,
          _qty: Number(p.cantidad) || 1,
        }))
      );
    }
  }, [partidas]);

  const totals = useMemo(() => {
    const sel = items.filter((i) => i._selected);
    const sum = sel.reduce((acc, p) => acc + (Number(p._qty) || 0) * (Number(p.precio_unitario) || 0), 0);
    return { count: sel.length, sum };
  }, [items]);

  const toggle = (id) =>
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, _selected: !i._selected } : i)));
  const setQty = (id, v) =>
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, _qty: v } : i)));

  const handleAdd = () => {
    const out = items
      .filter((i) => i._selected)
      .map((i) => ({ ...i, cantidad: Number(i._qty) || 0 }));
    onAdd(out);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-3xl p-0 gap-0 overflow-hidden"
        data-testid="ai-modal"
      >
        <div className="px-7 pt-7 pb-2">
          <DialogTitle asChild>
            <div className="flex items-center gap-2 text-blue-600 font-display text-2xl font-bold">
              <Sparkles className="w-6 h-6" />
              <span>Generar con IA</span>
            </div>
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500 mt-2">
            Escribe o dicta con detalle el trabajo y la IA generará un borrador de presupuesto para que revises y edites.
          </DialogDescription>
        </div>

        <div className="px-7 pb-3">
          <div className="rx-info-banner" data-testid="ai-warning-banner">
            <Info className="w-4 h-4 mt-[2px] flex-shrink-0" />
            <span>
              La IA puede cometer errores. Revisa siempre las partidas, cantidades y precios después de añadirlas al presupuesto.
            </span>
          </div>
        </div>

        {loading && (
          <div className="px-7 py-16 flex flex-col items-center justify-center text-slate-600 gap-3" data-testid="ai-loading">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <p className="text-sm">Generando partidas con Claude…</p>
            <p className="text-xs text-slate-400">Esto puede tardar 10-30 segundos</p>
          </div>
        )}

        {error && !loading && (
          <div className="px-7 py-10 flex flex-col items-center text-red-600" data-testid="ai-error">
            <AlertCircle className="w-7 h-7 mb-2" />
            <p className="text-sm font-medium">{error}</p>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            <div className="px-7 flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-blue-600 font-semibold">
                <span className="text-xl leading-none">+</span>
                <span>Añadir al presupuesto</span>
              </div>
              <div className="rx-pill" data-testid="ai-selection-pill">
                {totals.count} sel. · {fmtEUR(totals.sum)}
              </div>
            </div>

            <div className="px-7 pb-4 max-h-[440px] overflow-y-auto flex flex-col gap-3">
              {items.map((p) => (
                <PartidaRow key={p.id} p={p} onToggle={() => toggle(p.id)} onQty={(v) => setQty(p.id, v)} />
              ))}
            </div>
          </>
        )}

        <div className="px-7 py-4 border-t border-slate-200 bg-slate-50 flex justify-between gap-3">
          <Button
            variant="outline"
            onClick={onBack}
            data-testid="ai-back-btn"
            className="gap-2 border-slate-300"
          >
            <ArrowLeft className="w-4 h-4" /> Volver a describir
          </Button>
          <Button
            onClick={handleAdd}
            disabled={loading || totals.count === 0}
            data-testid="ai-add-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Añadir {totals.count} partidas
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PartidaRow({ p, onToggle, onQty }) {
  const subtotal = (Number(p._qty) || 0) * (Number(p.precio_unitario) || 0);
  return (
    <div
      className={`rx-card p-4 flex gap-4 items-start transition-all ${p._selected ? "border-blue-200 bg-blue-50/40" : ""}`}
      data-testid={`ai-partida-${p.codigo || p.id}`}
    >
      <button
        type="button"
        aria-label="Seleccionar partida"
        onClick={onToggle}
        className={`rx-checkbox-circle ${p._selected ? "checked" : ""}`}
        data-testid={`ai-partida-checkbox-${p.codigo || p.id}`}
      >
        {p._selected && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </button>

      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-900 text-[15px] leading-snug">{p.descripcion}</div>
        {p.is_estimation && (
          <div className="rx-warning-badge mt-2">Estimación IA · Revisar precio</div>
        )}
        <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
          <span className="font-medium text-slate-600">{p.categoria}</span>
          <span>·</span>
          <span>{fmtEUR(p.precio_unitario)} / {p.unidad}</span>
          {p.codigo && <><span>·</span><span className="font-mono">{p.codigo}</span></>}
        </div>
      </div>

      <div className="flex flex-col items-end gap-2 w-32">
        <Input
          type="number"
          step="0.01"
          min="0"
          value={p._qty}
          onChange={(e) => onQty(e.target.value)}
          className="h-9 text-right"
          data-testid={`ai-qty-${p.codigo || p.id}`}
        />
        <div className="text-blue-600 font-bold">{fmtEUR(subtotal)}</div>
      </div>
    </div>
  );
}
